import { useCallback, useMemo } from 'react';
import { useGet } from 'hooks';
import { aiSearchCredits } from 'services/api';
import { aiSearchProductName } from 'utils/constants';

/** Fallback banner when the daily limit is hit and the API sends no title (SCIL-14004). */
export function getDefaultSciSearchDailyLimitMessage(isTrialUser) {
  const team = isTrialUser ? 'Sales' : 'Customer Success';
  return `You're over your daily ${aiSearchProductName} limit; please contact ${team} if you have any questions.`;
}

/** Inline banner when short-window rate limit is hit (matches API copy when no title). */
export function getDefaultSciSearchRateLimitMessage() {
  return `You have made too many ${aiSearchProductName} queries in a short period. Please wait and try again.`;
}

/**
 * AI Search credits hook.
 *
 * Fetches GET /api/ai/search-credits and exposes:
 * - remaining: daily credits left (null until loaded)
 * - refresh: re-read the endpoint (call after each /api/ai/filters response)
 * - loading / error: raw request state
 *
 * Demo vs full licence UX uses `isFullTrial` from `utils/helpers/license` with `useAuth` — not this endpoint.
 */
export function useAiSearchCredits() {
  const [{ res, loading, error }, fetch] = useGet({ url: aiSearchCredits });

  const credits = useMemo(() => {
    if (!res) {
      return {
        remaining: null,
      };
    }
    return {
      remaining: typeof res.remaining === 'number' ? res.remaining : null,
    };
  }, [res]);

  const refresh = useCallback(() => {
    fetch();
  }, [fetch]);

  return {
    ...credits,
    loading,
    error,
    refresh,
  };
}

export default useAiSearchCredits;
